import { useEffect, useState } from 'react';
import { AlertTriangle, GitBranch } from 'lucide-react';
import { apiService, PackRoleSource } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import styles from '@/pages/GradesPage.module.scss';

/** Nom lisible d'un repo git à partir de son URL (…/PremiumResourcePack.git → PremiumResourcePack). */
function repoName(url: string | null): string {
  if (!url) return '—';
  const last = url.replace(/\.git$/, '').split('/').pop();
  return last || url;
}

/**
 * Rappelle dans quel pack du projet actif la page écrit (cosmetics ou atlas de glyphes) : les repos
 * diffèrent par projet (Paladium ≠ NationsGlory), donc on l'affiche au lieu de le laisser implicite.
 */
export function PackSourceNote({ role, page = 'F1' }: { role: 'cosmetics' | 'glyph'; page?: string }) {
  const project = useGameStore((s) => s.activeGameId);
  const [src, setSrc] = useState<PackRoleSource | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoaded(false);
    apiService.getCosmeticSources(page).then((r) => {
      if (!alive) return;
      setSrc(r.data ? (role === 'glyph' ? r.data.glyph : r.data.cosmetics) : null);
      setLoaded(true);
    });
    return () => { alive = false; };
  }, [project, role, page]);

  if (!loaded) return null;

  if (!src || !src.found) {
    return (
      <span className={styles.error}>
        <AlertTriangle size={14} /> Aucun pack {role === 'glyph' ? "d'atlas de glyphes" : 'de cosmetics'} pour ce projet.
        Marque le pack source dans Paramètres généraux → Pack, puis synchronise-le.
      </span>
    );
  }

  return (
    <span className={styles.muted}>
      <GitBranch size={13} /> Pack cible : <strong>{src.groupName || src.groupId}</strong> · {repoName(src.resourceRepo)}
      {src.branch ? ` (${src.branch})` : ''}
      {!src.explicit && ' · auto-détecté (non marqué dans Paramètres généraux → Pack)'}
      {src.readOnly && ' · lecture seule'}
    </span>
  );
}
